import { makeAutoObservable, runInAction } from 'mobx';

export interface CalculationHistory {
  id: string;
  expression: string;
  result: number;
  timestamp: number;
}

export interface MemorySlot {
  value: number;
  timestamp: number;
}

export class CalculatorStore {
  private display: string = '0';
  private expression: string = '';
  private isNewCalculation: boolean = true;
  private history: CalculationHistory[] = [];
  private memory: MemorySlot[] = Array(4).fill(null).map(() => ({ value: 0, timestamp: 0 }));
  private angleMode: 'degrees' | 'radians' = 'degrees';
  private showHistory: boolean = false;

  constructor() {
    makeAutoObservable(this);
    this.loadHistory();
    this.loadMemory();
  }

  // Display getters
  get currentDisplay(): string {
    return this.display;
  }

  get currentExpression(): string {
    return this.expression;
  }

  get calculationHistory(): CalculationHistory[] {
    return this.history.slice().reverse(); // Most recent first
  }

  get memorySlots(): MemorySlot[] {
    return this.memory;
  }

  get currentAngleMode(): 'degrees' | 'radians' {
    return this.angleMode;
  }

  get isHistoryVisible(): boolean {
    return this.showHistory;
  }

  // Basic operations
  inputNumber(num: string): void {
    runInAction(() => {
      if (this.isNewCalculation) {
        this.display = num;
        this.isNewCalculation = false;
      } else {
        if (this.display === '0' && num !== '.') {
          this.display = num;
        } else {
          this.display += num;
        }
      }
    });
  }

  inputDecimal(): void {
    runInAction(() => {
      if (this.isNewCalculation) {
        this.display = '0.';
        this.isNewCalculation = false;
      } else if (!this.display.includes('.')) {
        this.display += '.';
      }
    });
  }

  inputOperator(operator: string): void {
    runInAction(() => {
      if (this.expression && !this.isNewCalculation) {
        // Complete current calculation first
        this.calculate();
      }
      
      this.expression = this.display + ' ' + operator + ' ';
      this.isNewCalculation = true;
    });
  }

  calculate(): void {
    try {
      let fullExpression = this.expression + this.display;
      
      if (!fullExpression.trim()) {
        return;
      }

      // Replace display operators with JavaScript operators
      fullExpression = fullExpression
        .replace(/×/g, '*')
        .replace(/÷/g, '/')
        .replace(/\^/g, '**');

      // Evaluate the expression
      const result = this.evaluateExpression(fullExpression);
      
      if (isNaN(result) || !isFinite(result)) {
        throw new Error('Invalid calculation');
      }

      // Add to history
      const historyItem: CalculationHistory = {
        id: Date.now().toString(),
        expression: fullExpression,
        result,
        timestamp: Date.now()
      };

      runInAction(() => {
        this.display = this.formatResult(result);
        this.expression = '';
        this.isNewCalculation = true;
        this.history.push(historyItem);
      });

      this.saveHistory();
    } catch (error) {
      runInAction(() => {
        this.display = 'Error';
        this.expression = '';
        this.isNewCalculation = true;
      });
    }
  }

  // Scientific functions
  applyFunction(func: string): void {
    try {
      const value = parseFloat(this.display);
      let result: number;

      switch (func) {
        case 'sin':
          result = Math.sin(this.angleMode === 'degrees' ? this.toRadians(value) : value);
          break;
        case 'cos':
          result = Math.cos(this.angleMode === 'degrees' ? this.toRadians(value) : value);
          break;
        case 'tan':
          result = Math.tan(this.angleMode === 'degrees' ? this.toRadians(value) : value);
          break;
        case 'asin':
          result = this.angleMode === 'degrees' ? this.toDegrees(Math.asin(value)) : Math.asin(value);
          break;
        case 'acos':
          result = this.angleMode === 'degrees' ? this.toDegrees(Math.acos(value)) : Math.acos(value);
          break;
        case 'atan':
          result = this.angleMode === 'degrees' ? this.toDegrees(Math.atan(value)) : Math.atan(value);
          break;
        case 'log':
          result = Math.log10(value);
          break;
        case 'ln':
          result = Math.log(value);
          break;
        case 'sqrt':
          result = Math.sqrt(value);
          break;
        case 'square':
          result = value * value;
          break;
        case 'cube':
          result = value * value * value;
          break;
        case 'reciprocal':
          result = 1 / value;
          break;
        case 'factorial':
          result = this.factorial(Math.floor(Math.abs(value)));
          break;
        case 'abs':
          result = Math.abs(value);
          break;
        case 'negate':
          result = -value;
          break;
        default:
          throw new Error('Unknown function');
      }

      runInAction(() => {
        this.display = this.formatResult(result);
        this.isNewCalculation = true;
      });
    } catch (error) {
      runInAction(() => {
        this.display = 'Error';
        this.isNewCalculation = true;
      });
    }
  }

  // Memory operations
  memoryClear(slot: number = 0): void {
    runInAction(() => {
      this.memory[slot] = { value: 0, timestamp: Date.now() };
    });
    this.saveMemory();
  }

  memoryStore(slot: number = 0): void {
    const value = parseFloat(this.display);
    runInAction(() => {
      this.memory[slot] = { value, timestamp: Date.now() };
    });
    this.saveMemory();
  }

  memoryRecall(slot: number = 0): void {
    runInAction(() => {
      this.display = this.formatResult(this.memory[slot].value);
      this.isNewCalculation = true;
    });
  }

  memoryAdd(slot: number = 0): void {
    const value = parseFloat(this.display);
    runInAction(() => {
      this.memory[slot].value += value;
      this.memory[slot].timestamp = Date.now();
    });
    this.saveMemory();
  }

  memorySubtract(slot: number = 0): void {
    const value = parseFloat(this.display);
    runInAction(() => {
      this.memory[slot].value -= value;
      this.memory[slot].timestamp = Date.now();
    });
    this.saveMemory();
  }

  // Utility functions
  clear(): void {
    runInAction(() => {
      this.display = '0';
      this.expression = '';
      this.isNewCalculation = true;
    });
  }

  clearEntry(): void {
    runInAction(() => {
      this.display = '0';
      this.isNewCalculation = true;
    });
  }

  backspace(): void {
    runInAction(() => {
      if (this.display.length > 1) {
        this.display = this.display.slice(0, -1);
      } else {
        this.display = '0';
        this.isNewCalculation = true;
      }
    });
  }

  toggleAngleMode(): void {
    runInAction(() => {
      this.angleMode = this.angleMode === 'degrees' ? 'radians' : 'degrees';
    });
  }

  toggleHistory(): void {
    runInAction(() => {
      this.showHistory = !this.showHistory;
    });
  }

  clearHistory(): void {
    runInAction(() => {
      this.history = [];
    });
    this.saveHistory();
  }

  useHistoryResult(result: number): void {
    runInAction(() => {
      this.display = this.formatResult(result);
      this.isNewCalculation = true;
    });
  }

  // Helper methods
  private evaluateExpression(expr: string): number {
    // Basic expression evaluator - in production, use a proper parser
    // This is simplified for demo purposes
    return Function('"use strict"; return (' + expr + ')')();
  }

  private formatResult(num: number): string {
    if (Math.abs(num) > 1e15 || (Math.abs(num) < 1e-6 && num !== 0)) {
      return num.toExponential(6);
    }
    
    // Remove trailing zeros and unnecessary decimal point
    const result = num.toString();
    if (result.includes('.')) {
      return result.replace(/\.?0+$/, '');
    }
    return result;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  private toDegrees(radians: number): number {
    return radians * (180 / Math.PI);
  }

  private factorial(n: number): number {
    if (n < 0 || n > 170) throw new Error('Factorial out of range');
    if (n <= 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  }

  // Persistence
  private saveHistory(): void {
    localStorage.setItem('calculator-history', JSON.stringify(this.history));
  }

  private loadHistory(): void {
    try {
      const saved = localStorage.getItem('calculator-history');
      if (saved) {
        const history = JSON.parse(saved);
        runInAction(() => {
          this.history = history || [];
        });
      }
    } catch (error) {
      console.warn('Failed to load calculator history:', error);
    }
  }

  private saveMemory(): void {
    localStorage.setItem('calculator-memory', JSON.stringify(this.memory));
  }

  private loadMemory(): void {
    try {
      const saved = localStorage.getItem('calculator-memory');
      if (saved) {
        const memory = JSON.parse(saved);
        runInAction(() => {
          this.memory = memory || Array(4).fill(null).map(() => ({ value: 0, timestamp: 0 }));
        });
      }
    } catch (error) {
      console.warn('Failed to load calculator memory:', error);
    }
  }
}

export const calculatorStore = new CalculatorStore();
